/**
 * Κοινό API layer του dine-in.
 *
 * Το ταμείο και το app σερβιτόρου έχουν ΞΕΧΩΡΙΣΤΟ UI αλλά μιλάνε στο backend
 * μέσα από ΕΝΑ σημείο. Έτσι μια αλλαγή endpoint (ή ένας νέος κωδικός λάθους)
 * διορθώνεται μία φορά, και οι δύο οθόνες δεν αποκλίνουν σε συμπεριφορά.
 */

import { API_BASE_URL } from '../components/config/api';
import type { PosOptionGroup } from '../types/order';
import type { BillSummary } from '../utils/dineInMoney';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('xentrix_token')}`,
});

const jsonHeaders = (): Record<string, string> => ({
  ...authHeaders(),
  'Content-Type': 'application/json',
});

/** Σφάλμα με το σώμα της απάντησης — τα endpoints επιστρέφουν κωδικούς
 *  (π.χ. `code: 'receipt_issued'`, `feature_disabled`) που το UI χρειάζεται. */
export class DineInApiError extends Error {
  status: number;
  code?: string;
  body: any;

  constructor(status: number, body: any) {
    super(body?.message || body?.error || `HTTP ${status}`);
    this.name = 'DineInApiError';
    this.status = status;
    this.code = body?.code;
    this.body = body ?? {};
  }
}

async function request<T = any>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new DineInApiError(response.status, body);
  return body as T;
}

const get = <T = any>(path: string) => request<T>(path, { headers: authHeaders() });

const post = <T = any>(path: string, payload?: any) =>
  request<T>(path, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload ?? {}),
  });

const put = <T = any>(path: string, payload?: any) =>
  request<T>(path, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(payload ?? {}),
  });

const del = <T = any>(path: string) => request<T>(path, { method: 'DELETE', headers: authHeaders() });

/** Ό,τι μπορεί να αλλάξει ταυτόχρονα σε πολλά τραπέζια. */
export interface TableBulkUpdates {
  capacity?: number;
  shape?: 'round' | 'square' | 'rectangle';
  width?: number;
  height?: number;
  is_reservable?: boolean;
  zone_id?: number;
}

export interface TableDraft {
  table_number: string;
  name?: string | null;
  capacity: number;
  shape: 'round' | 'square' | 'rectangle';
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  is_reservable: boolean;
}

/**
 * Διαχείριση κάτοψης (ζώνες + τραπέζια) — ο editor του διαχειριστή.
 * Τα σφάλματα έρχονται ως DineInApiError: `code` + `body.conflicts` όπου έχει νόημα.
 */
export const floorPlanApi = {
  zones: (storeId: number) => get<any[]>(`/api/dine-in/${storeId}/zones`),

  /** Όλα τα τραπέζια του καταστήματος. Ο αριθμός τραπεζιού είναι μοναδικός σε
   *  όλο το κατάστημα, άρα ο editor τα χρειάζεται όλα, όχι μόνο της ζώνης. */
  tables: (storeId: number) => get<any[]>(`/api/dine-in/${storeId}/tables`),

  createZone: (storeId: number, payload: { name: string; printer_id: number | null }) =>
    post(`/api/dine-in/${storeId}/zones`, payload),

  updateZone: (storeId: number, zoneId: number, payload: { name?: string; printer_id?: number | null }) =>
    put(`/api/dine-in/${storeId}/zones/${zoneId}`, payload),

  deleteZone: (storeId: number, zoneId: number) => del(`/api/dine-in/${storeId}/zones/${zoneId}`),

  /** Όλα ή κανένα. 409 `table_numbers_exist` με `body.conflicts`. */
  createTables: (storeId: number, zoneId: number, tables: TableDraft[]) =>
    post<{ tables: any[] }>(`/api/dine-in/${storeId}/zones/${zoneId}/tables/bulk`, { tables }),

  /** Αριθμός / όνομα ενός τραπεζιού (τα μόνα πεδία που δεν έχουν νόημα μαζικά). */
  updateTable: (storeId: number, tableId: number, payload: { table_number?: string; name?: string | null }) =>
    put(`/api/dine-in/${storeId}/tables/${tableId}`, payload),

  updateTables: (storeId: number, ids: number[], updates: TableBulkUpdates) =>
    put<{ tables: any[] }>(`/api/dine-in/${storeId}/tables/bulk`, { ids, updates }),

  saveLayout: (
    storeId: number,
    tables: Array<{ id: number; position_x: number; position_y: number; width: number; height: number }>
  ) => put(`/api/dine-in/${storeId}/tables/positions`, { tables }),

  /** Τραπέζια με ιστορικό λογαριασμών ΔΕΝ σβήνονται· γυρίζουν στο `skipped`. */
  deleteTables: (storeId: number, ids: number[]) =>
    post<{ deleted: number[]; skipped: Array<{ id: number; table_number: string; reason: 'active_session' | 'has_history' }> }>(
      `/api/dine-in/${storeId}/tables/bulk-delete`, { ids }
    ),

  /** Οι εκτυπωτές του καταστήματος, για τον επιλογέα ανά ζώνη. */
  printers: (storeId: number) => get<any>(`/api/stores/${storeId}/printers`),
};

/** Διάστημα της αναφοράς κερασμάτων: η τρέχουσα βάρδια ('current') ή ένα κλείσιμο ταμείου (id). */
export interface TreatsPeriod {
  key: string;
  start: string;
  /** null = ως τώρα (τρέχουσα βάρδια). */
  end: string | null;
  closedByName: string | null;
}

export interface TreatsReportLine {
  productId: number | null;
  sizeId: number | null;
  name: string;
  size: string | null;
  quantity: number;
  /** Αξία σε τιμές καταλόγου (με ΦΠΑ), πριν το κέρασμα. */
  value: number;
  /** Πόσα από αυτά ήρθαν από έκπτωση 100% (στο είδος, στην παραγγελία ή στον λογαριασμό). */
  discountQuantity?: number;
  discountValue?: number;
}

export interface TreatsReport {
  period: TreatsPeriod;
  periods: TreatsPeriod[];
  lines: TreatsReportLine[];
  totals: { quantity: number; value: number; discountQuantity?: number; discountValue?: number };
  generatedAt: string;
}

/** Αναφορά κερασμάτων («Κεράσματα» στο μενού του ταμία). Gated στο dine_in_settings.treats_report_enabled
 *  (403 treats_report_disabled) και στο δικαίωμα της Χ αναφοράς (403 permission_denied). */
export const treatsReportApi = {
  get: (storeId: number, period = 'current') =>
    get<TreatsReport>(`/api/dine-in/${storeId}/reports/treats?period=${encodeURIComponent(period)}`),

  /** Στον εκτυπωτή αποδείξεων· printed=false όταν δεν υπάρχει ενεργός εκτυπωτής. */
  print: (storeId: number, period = 'current') =>
    post<TreatsReport & { printed: boolean; message: string }>(`/api/dine-in/${storeId}/reports/treats/print`, { period }),
};

/** Μία πληρωμή τραπεζιού όπως τη μετρά ο server (GET …/sessions/:id/payments). */
export interface TablePayment {
  id: number;
  /** 'cash' | 'card' | 'iris' | 'credit' (Επί πιστώσει) | 'other' */
  type: string;
  amount: number;
  processedAt: string | null;
  processedByName: string | null;
  /** Ακυρωμένη πληρωμή (ακύρωση συνεδρίας ή λάθος πληρωμή) — δεν μετρά πουθενά. */
  voidedAt: string | null;
  voidedReason: string | null;
  voidedByName?: string | null;
  /** Ακυρώνεται τώρα από τον ταμία (μετρητά ή «Επί πιστώσει», ανοιχτό ταμείο, όχι ήδη ακυρωμένη). */
  voidable?: boolean;
  /** Γιατί όχι: 'voided' | 'not_cash' (κάρτα/IRIS → επιστροφή στο τερματικό) | 'closed_period'. */
  voidBlock?: string | null;
}

export interface TablePaymentsSummary {
  sessionId: number;
  sessionStatus: string;
  total: number;
  /** Μη ακυρωμένες πληρωμές. */
  paid: number;
  remaining: number;
  finalDocument: {
    id: number;
    invoiceTypeCode: string;
    status: string;
    series: string;
    number: number | string;
    isInvoice: boolean;
  } | null;
  /** Ακύρωση λάθος πληρωμής (μετρητά / «Επί πιστώσει»): πάντα ενεργή από 6/10/2026 + αρχή του ανοιχτού ταμείου. */
  void?: { enabled: boolean; periodStart: string | null };
  payments: TablePayment[];
}

export interface VoidPaymentResult {
  success: boolean;
  message: string;
  paymentId: number;
  sessionId: number;
  amount: number;
  paid: number;
  total: number;
  remaining: number;
  /** Η συνεδρία είχε κλείσει ως εξοφλημένη και ξανάνοιξε. */
  reopened: boolean;
  sessionStatus: string;
  tableStatus: string | null;
}

/** Πληρωμές συνεδρίας στην οθόνη του ταμία (όχι PDA). Το «Επί πιστώσει» καταγράφεται από το
 *  cash-payment (paymentMethod 'credit'), όπως τα μετρητά — όχι από εδώ. */
export const tablePaymentsApi = {
  summary: (storeId: number, sessionId: number) =>
    get<TablePaymentsSummary>(`/api/dine-in/${storeId}/sessions/${sessionId}/payments`),

  /** Ακύρωση ΜΙΑΣ λάθος πληρωμής (μετρητά ή «Επί πιστώσει»), με προαιρετικό λόγο (κενός = κανένας) —
   *  χωρίς διακόπτη από 6/10/2026, με το δικαίωμα dine_in_void_payment (403 χωρίς αυτό)· 400 reason_too_long ·
   *  409 not_cash (κάρτα/IRIS → επιστροφή στο τερματικό) / closed_period (κλεισμένο Ζ) /
   *  table_has_new_session / already_voided / payment_in_progress. */
  voidPayment: (storeId: number, paymentId: number, reason: string) =>
    post<VoidPaymentResult>(`/api/dine-in/${storeId}/payments/${paymentId}/void`, { reason }),
};

export const dineInApi = {
  /** Τραπέζια του υπαλλήλου. Σέβεται το table_responsibility_enabled: αν είναι
   *  ενεργό επιστρέφει μόνο τα ανατεθειμένα, αλλιώς όλα. Κάθε τραπέζι φέρνει
   *  current_session_id / guest_count / session_total όταν έχει ανοιχτή σέσια. */
  myTables: (storeId: number) =>
    get<{ tableResponsibilityEnabled: boolean; tables: any[] }>(
      `/api/dine-in/${storeId}/my-tables`
    ),

  /** Λεπτομέρειες τραπεζιού: table, session, orders (με items), paidItems.
   *  ΠΡΟΣΟΧΗ: επιστρέφει ΚΑΙ ακυρωμένα/κερασμένα — φιλτράρισμα με το
   *  utils/dineInMoney πριν από οποιονδήποτε υπολογισμό. */
  tableDetails: (storeId: number, tableId: number) =>
    get<{ table: any; session: any | null; orders: any[]; paidItems: any[] }>(
      `/api/dine-in/${storeId}/tables/${tableId}/details`
    ),

  /** Άνοιγμα σέσιας σε ελεύθερο τραπέζι. Με `items` (ο πρώτος γύρος, ίδιο σώμα με το
   *  createOrder) ο server ελέγχει πρώτα το κατώτατο: 403 `discount_forbidden` και ΚΑΜΙΑ σέσια,
   *  αντί για τραπέζι «κατειλημμένο» με 0 € που ο σερβιτόρος δεν μπορεί να ακυρώσει. */
  openSession: (storeId: number, tableId: number, guestCount: number, items?: any[]) =>
    post(`/api/dine-in/${storeId}/sessions`, {
      table_id: tableId, guest_count: guestCount, ...(items && items.length ? { items } : {}),
    }),

  /** Νέος γύρος παραγγελίας. 409 `receipt_issued` αν έχει ήδη κοπεί απόδειξη
   *  (η σέσια δεν είναι πλέον 'open') — το UI πρέπει να το εξηγεί, όχι να σκάει.
   *  Η έκπτωση παραγγελίας απαιτεί permission dine_in_apply_discount (403). */
  createOrder: (
    storeId: number,
    sessionId: number,
    payload: {
      items: any[];
      notes?: string;
      order_discount_type?: 'percentage' | 'amount' | null;
      order_discount_value?: number;
      /** Μηνύματα κουζίνας μαζί με τον γύρο — μόνο όταν υπάρχουν (κλειστή ρύθμιση: ο server τα αγνοεί). */
      kitchen_messages?: string[];
    }
  ) => post(`/api/dine-in/${storeId}/sessions/${sessionId}/orders`, payload),

  /** Ready → served. */
  serveOrder: (storeId: number, orderId: number) =>
    put(`/api/dine-in/${storeId}/orders/${orderId}/serve`),

  /** Έκδοση απόδειξης για το τραπέζι, από το κινητό του σερβιτόρου.
   *
   *  ΚΛΕΙΔΩΝΕΙ το τραπέζι: μετά από αυτό δεν επιτρέπονται νέοι γύροι, εκπτώσεις
   *  ή μεταφορές — μόνο πληρωμή. Θέλει δικαίωμα dine_in_print_receipt.
   *  Το χαρτί βγαίνει στον εκτυπωτή της ζώνης του τραπεζιού, αν έχει οριστεί. */
  issueReceipt: (storeId: number, sessionId: number) =>
    post(`/api/dine-in/${storeId}/sessions/${sessionId}/receipt`),

  /** Λογαριασμός τραπεζιού (ΟΧΙ απόδειξη) στον εκτυπωτή της ζώνης.
   *  Gated στο dine_in_settings.bill_print_enabled (403 feature_disabled).
   *  409 bill_stale όταν το τραπέζι άλλαξε από τότε που φορτώθηκε η οθόνη. */
  printBill: (storeId: number, sessionId: number, bill: BillSummary) =>
    post<{ ok: boolean; printer: string }>(`/api/dine-in/${storeId}/sessions/${sessionId}/print-bill`, bill),

  /** Κλείσιμο τραπεζιού με σύνολο 0 € (όλα κερασμένα / έκπτωση 100%): χωρίς απόδειξη και είσπραξη.
   *  409 open_order_document όταν μένει ανοιχτό Δελτίο Παραγγελίας στο myDATA· θέλει dine_in_process_payment. */
  closeSession: (storeId: number, sessionId: number) =>
    put<{ message?: string }>(`/api/dine-in/${storeId}/sessions/${sessionId}/close`),

  /** Μήνυμα κουζίνας για το τραπέζι («ΠΑΣΟ ΚΥΡΙΩΣ» …): μικρό δελτίο σε όλους τους εκτυπωτές
   *  κουζίνας. Gated στο dine_in_settings.kitchen_messages_enabled (403 feature_disabled)·
   *  503 no_printer χωρίς εκτυπωτή κουζίνας. */
  sendKitchenMessage: (storeId: number, sessionId: number, messages: string[]) =>
    post<{ ok: boolean; messages: string[]; printers: string[] }>(
      `/api/dine-in/${storeId}/sessions/${sessionId}/kitchen-message`, { messages }
    ),

  /** Σύνοψη ημέρας του υπαλλήλου (μετρητά/κάρτα κρύβονται χωρίς
   *  dine_in_view_registers στο UI). */
  mySummary: (storeId: number) =>
    get<{ cash_total: number; card_total: number; orders_count: number; tables_served: number }>(
      `/api/dine-in/${storeId}/my-summary`
    ),

  /** Ρυθμίσεις dine-in του καταστήματος (toggles λειτουργιών). */
  settings: (storeId: number) => get<any>(`/api/dine-in/${storeId}/settings`),

  /** Οι σημερινές κρατήσεις (Xentrix Sala) — για τη σήμανση των τραπεζιών. */
  reservationsToday: (storeId: number) => get<any[]>(`/api/dine-in/${storeId}/reservations/today`),

  /** Ομάδες επιλογών ενός είδους — το ίδιο endpoint με την οθόνη τραπεζιών του ταμία
   *  (σκέτος πίνακας, όχι { groups }). Οι τιμές επιλογών γίνονται αριθμοί: ένα NUMERIC ως
   *  κείμενο θα κολλούσε στο άθροισμα («9.50» + «0.30»). Το app σερβιτόρου το καλεί στην πρώτη
   *  προσθήκη κάθε είδους (επιλογές & «χωρίς» στο PDA πάντα ενεργές από 6/10/2026). */
  productOptionGroups: async (productId: number): Promise<PosOptionGroup[]> => {
    const data = await get<any>(`/api/products/${productId}/option-groups`);
    if (!Array.isArray(data)) return [];
    return data.map((g: any) => ({
      ...g,
      is_required: g.is_required === true,
      selection_type: g.selection_type === 'multiple' ? 'multiple' : 'single',
      choices: (Array.isArray(g.choices) ? g.choices : []).map((c: any) => ({
        ...c,
        price_modifier: Number(c.price_modifier) || 0,
        is_default: c.is_default === true,
      })),
    }));
  },

  /** Πληρωμή μετρητοίς. Το ποσό επικυρώνεται server-side με ανοχή μισού λεπτού
   *  έναντι του υπολοίπου (αφαιρώντας ό,τι έχει ήδη πληρωθεί). paymentMethod 'credit' =
   *  «Επί πιστώσει», ΜΟΝΟ από το ταμείο (όχι από το PDA)· χωρίς τιμή → μετρητά. */
  cashPayment: (
    storeId: number,
    sessionId: number,
    payload: {
      amount: number;
      items: Array<{ orderId: number; itemId: number; quantity: number; price?: number }>;
      paymentMethod?: 'cash' | 'credit';
    }
  ) => post(`/api/dine-in/${storeId}/sessions/${sessionId}/cash-payment`, payload),

  /** Πληρωμή με κάρτα ή IRIS μέσω τερματικού POS. */
  cardPayment: (
    storeId: number,
    sessionId: number,
    payload: {
      amount: number;
      items: Array<{ orderId: number; itemId: number; quantity: number; price?: number }>;
      terminalId?: number | null;
      isIris?: boolean;
    }
  ) => post(`/api/dine-in/${storeId}/sessions/${sessionId}/card-payment`, payload),

  /** Ενεργά τερματικά POS του καταστήματος. */
  posTerminals: (storeId: number) => get<any[]>(`/api/stores/${storeId}/pos/terminals`),
};

export default dineInApi;
